import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { getIcon } from '@/common/lib/getIcon';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const STATIC_IMG: Record<string, string> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  boardStarPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Record<string, 'tgem' | 'dice' | 'star' | 'cash'> = {
  tgem: 'tgem',
  dice: 'dice',
  cash: 'cash',
};

const REWARD_LABELS: Record<string, string> = {
  tgem: 'T-Gem',
  dice: 'Dice',
  starPoints: 'SP',
  boardStarPoints: 'SP',
  goldTicket: 'Gold',
  silverTicket: 'Silver',
  diamondTicket: 'Diamond',
  bingoBalls: 'Balls',
  cash: 'Cash',
};

function getRewardIcon(type: string): string | null {
  if (STATIC_IMG[type]) return STATIC_IMG[type];
  const icon = getIcon(type);

  if (icon) return icon.webp ?? icon.png;

  return null;
}

const BingoPotentialRewards: FC = () => {
  const { t } = useTranslation();
  const superRewardOptions = useAppSelector(
    (state) => state.bingo.superRewardOptions,
  );

  if (!superRewardOptions.length) return null;

  return (
    <div className={css.wrapper}>
      <img
        src="/img/bingo/super-prize-bg.png"
        alt=""
        className={css.bg}
        aria-hidden
      />

      <div className={css.chest}>
        <motion.img
          src="/img/bingo/chest-purple.png"
          alt="chest"
          className={css.chestImg}
          animate={{ y: [0, -2, 0] }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <div className={css.body}>
        <div className={css.stack}>
          <p className={css.hint}>{t('Get one of the rewards')}</p>

          <div className={css.rewards}>
            {superRewardOptions.map((reward) => {
              const currencyKind = CURRENCY_MAP[reward.type];
              const imgSrc = !currencyKind ? getRewardIcon(reward.type) : null;
              const label =
                REWARD_LABELS[reward.type] ?? reward.type.replace(/_/g, ' ');

              return (
                <div key={reward.type} className={css.rewardItem}>
                  <div className={css.iconWrap}>
                    {currencyKind ? (
                      <CurrencyImage
                        kind={currencyKind}
                        size="32px"
                        imageVariant="large"
                      />
                    ) : imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={reward.type}
                        className={css.rewardIcon}
                      />
                    ) : null}
                  </div>
                  <div className={css.value}>
                    <span className={css.amount}>{reward.value}</span>
                    <span className={css.label}>{label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export { BingoPotentialRewards };
